/*

* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

*

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/

import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis, kcOIDCAuthApiRef } from './apis';
import { entityPage } from './components/catalog/catalogEntity/EntityPage';
import { TechDocsIndexPage as TechDocsIndexPageCustom } from './components/techDocs/TechDocsIndexPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import { AlertDisplay, OAuthRequestDialog } from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { UnifiedThemeProvider } from '@backstage/theme';
import { CssBaseline } from '@material-ui/core';
import { bhTheme } from './theme/bh';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import { SignInPage } from './components/signIn/SignInPage';
import GrafanaIcon from './components/icons/GrafanaIcon';
import RallyIcon from './components/icons/RallyIcon';
import BlackDuckIcon from './components/icons/BlackDuckIcon';
import TrivyIcon from './components/icons/TrivyIcon';
import CheckMarxIcon from './components/icons/CheckMarxIcon';
import ConfluenceIcon from './components/icons/ConfluenceIcon';
import DynatraceIcon from './components/icons/DynatraceIcon';
import JenkinsIcon from './components/icons/JenkinsIcon';
import ServiceIcon from './components/icons/ServiceIcon';
import ExtensionIcon from '@material-ui/icons/ExtensionOutlined';
import WebsiteIcon from '@material-ui/icons/LanguageOutlined';
import { CatalogPage } from './components/catalog/CatalogPage';
import {
  CustomTechDocStyle,
} from '@internal/plugin-bh-techdocs-addons';
import { SettingsPage } from './components/userSettings/SettingsPage';

const app = createApp({
  apis,
  icons: {
    grafana: GrafanaIcon,
    rally: RallyIcon,
    confluence: ConfluenceIcon,
    jenkins: JenkinsIcon,
    dynatrace: DynatraceIcon,
    'type:service': ServiceIcon,
    'type:openapi': ExtensionIcon,
    'type:website': WebsiteIcon,
    blackduckScanReport: BlackDuckIcon,
    trivyScanReport: TrivyIcon,
    checkMarxScanReport: CheckMarxIcon,
  },
  components: {
    SignInPage: props => {
      // Use single provider since Keycloak handles Okta federation
      return (
        <SignInPage
          {...props}
          align="center"
          provider={{
            id: "keycloak",
            title: "",
            message: "Sign in with your corporate account",
            apiRef: kcOIDCAuthApiRef,
          }}
        />
      );
    },
  },
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  themes: [
    {
      id: 'default',
      title: 'Default',
      variant: 'light',
      Provider: ({ children }: { children: React.ReactNode }) => (
        <UnifiedThemeProvider theme={bhTheme}>
          <CssBaseline />
          {children}
        </UnifiedThemeProvider>
      ),
    },
  ],
});

const routes = (
  <FlatRoutes>
    {/* <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route> */}
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/catalog/:domain?" element={<CatalogIndexPage />}>
      <CatalogPage />
    </Route>
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs/:domain?" element={<TechDocsIndexPage />}>
      <TechDocsIndexPageCustom />
    </Route>
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        {/* <ReportIssue /> */}
        {/* <EditDocLink /> */}
        {/* <BookmarkDocLink /> */}
        <CustomTechDocStyle />
      </TechDocsAddons>
    </Route>
    {/* <Route path="/create" element={<ScaffolderPage />} /> */}
    {/* <Route path="/api-docs" element={<ApiExplorerPage />} /> */}
    {/* <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    /> */}
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />}>
      {SettingsPage}
    </Route>
    <Route
      path="/catalog-graph"
      element={
        <CatalogGraphPage
          initialState={{
            maxDepth: 1,
            selectedKinds: [
              'component',
              'domain',
              'system',
              'api',
              'group',
              'resource',
            ],
            selectedRelations: [
              RELATION_HAS_PART,
              RELATION_PART_OF,
              RELATION_DEPENDS_ON,
              RELATION_DEPENDENCY_OF,
              RELATION_CONSUMES_API,
              RELATION_API_CONSUMED_BY,
              RELATION_PROVIDES_API,
              RELATION_API_PROVIDED_BY,
            ],
          }}
        />
      }
    />
    {/* <Route path="/tools" element={<ExplorePage />}>
      {explorePage}
    </Route> */}
    {/* <Route path="/collaboration" element={<QetaPage title="Posts" />} />
    <Route path="/notifications" element={<NotificationsPage />} /> */}
    {/* <Route path="/entity-validation" element={<EntityValidationPage />} /> */}
    {/* <Route path='/dashboard' element={<Dashboard/>} /> */}
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      {/* <VisitListenerCustom /> */}
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
